import { type App, type Plugin, isRef, ref, watchEffect } from 'vue';
import { createI18n, type I18n } from 'vue-i18n';

import { useAppOptionsStore } from '@lib/stores/app-options';

/** The language when neither the user nor the browser names one the app has. */
export const DEFAULT_LOCALE = 'de';

/**
 * The language to show: the one the user picked, else the first browser language the app
 * has messages for (`de-AT` counts as `de`), else DEFAULT_LOCALE.
 */
export function resolveLocale(available: readonly string[], picked: string | null, browser: readonly string[]): string {
	if (picked && available.includes(picked)) return picked;
	for (const tag of browser) {
		const lang = tag.toLowerCase().split(/[-_]/)[0];
		if (available.includes(lang)) return lang;
	}
	return DEFAULT_LOCALE;
}

function browserLanguages(): readonly string[] {
	const nav = globalThis.navigator;
	if (!nav) return [];
	return nav.languages?.length ? nav.languages : [nav.language];
}

/**
 * vue-i18n for an eflib app. Install it after pinia. Until the user picks a language in the
 * profile menu, the app follows the browser's, also when that changes while the app is open.
 * `global` is vue-i18n's, for `t()` outside components.
 */
export function createEfI18n(messages: Record<string, any>): Plugin & { global: I18n['global'] } {
	const i18n = createI18n({
		locale: DEFAULT_LOCALE,
		fallbackLocale: 'en',
		globalInjection: true,
		messages,
	});

	return {
		global: i18n.global,
		install(app: App) {
			app.use(i18n);

			const appOptions = useAppOptionsStore();
			const browser = ref(browserLanguages());
			globalThis.addEventListener?.('languagechange', () => (browser.value = browserLanguages()));

			// The global locale is a ref in composition mode and a plain property in legacy mode.
			const global = i18n.global as unknown as { locale: string | { value: string } };
			watchEffect(() => {
				const locale = resolveLocale(Object.keys(messages), appOptions.appLocale, browser.value);
				if (isRef(global.locale)) global.locale.value = locale;
				else global.locale = locale;
			});
		},
	};
}

/** The same as a plugin, like the lib's others: `app.use(I18n, messages)`. */
export default {
	install(app: App, messages: Record<string, any>) {
		app.use(createEfI18n(messages));
	},
};
